import { Injectable } from '@nestjs/common';

// Presence is derived from traffic, not stored: the agent inbox polls its
// endpoints every few seconds while open, and each authenticated request is
// a heartbeat. No sockets, no schema — and deliberately in-memory: a restart
// resets everyone to offline until their next poll (seconds away), which is
// the correct failure mode for a liveness signal. Would need Redis only when
// chat-service runs more than one instance.

export const ONLINE_WINDOW_MS = 30_000;
export const AWAY_WINDOW_MS = 10 * 60_000;
export const TYPING_TTL_MS = 4_000;

export type AgentAvailability = 'online' | 'busy';
export type PresenceStatus = 'online' | 'busy' | 'away' | 'offline';

export interface AgentPresence {
  status: PresenceStatus;
  // ISO string so it serialises cleanly; null when no agent was ever seen.
  lastActiveAt: string | null;
  agentName?: string;
}

interface AgentMarker {
  lastActiveAt: number;
  agentName: string;
  availability: AgentAvailability;
}

// Platform-wide agents (applicationId null) park their marker under this key;
// the read side treats it as covering every application.
const GLOBAL_KEY = '*';

@Injectable()
export class PresenceService {
  // Store-wide: "is *someone* on this store's team online" — the shopper's
  // fallback when a session hasn't been picked up by a specific agent yet.
  private readonly agents = new Map<string, AgentMarker>();
  // Per-agent (keyed by the JWT sub / ServiceRequest.assignedAgentId): "is
  // *this specific* agent online" — what a shopper whose ticket has been
  // picked up should actually see instead of a generic store-wide signal.
  private readonly agentsById = new Map<string, AgentMarker>();
  private readonly typing = new Map<string, number>();
  private readonly typingAgentNames = new Map<string, string>();

  markAgentActive(applicationId: string | null, agentId: string, agentName: string): void {
    const key = applicationId ?? GLOBAL_KEY;
    const existing = this.agents.get(key);

    this.agents.set(key, {
      lastActiveAt: Date.now(),
      agentName,
      // A heartbeat refreshes recency but never overrides a manual Busy —
      // only setAgentStatus does that.
      availability: existing?.availability ?? 'online',
    });

    const existingById = this.agentsById.get(agentId);
    this.agentsById.set(agentId, {
      lastActiveAt: Date.now(),
      agentName,
      availability: existingById?.availability ?? 'online',
    });
  }

  setAgentStatus(applicationId: string | null, agentId: string, availability: AgentAvailability, agentName: string): void {
    this.agents.set(applicationId ?? GLOBAL_KEY, { lastActiveAt: Date.now(), agentName, availability });
    this.agentsById.set(agentId, { lastActiveAt: Date.now(), agentName, availability });
  }

  agentPresence(applicationId: string): AgentPresence {
    // Whichever marker (store-scoped or platform-wide) was active most
    // recently speaks for this application.
    const candidates = [this.agents.get(applicationId), this.agents.get(GLOBAL_KEY)].filter(
      (m): m is AgentMarker => m !== undefined,
    );

    if (candidates.length === 0) return { status: 'offline', lastActiveAt: null };

    const marker = candidates.sort((a, b) => b.lastActiveAt - a.lastActiveAt)[0];
    return this.toPresence(marker);
  }

  // Whether a specific agent (e.g. a ticket's assignedAgentId) is online —
  // falls back to just their last-known name (still useful to display, even
  // offline) when they've never been seen at all.
  agentPresenceById(agentId: string, fallbackName?: string): AgentPresence {
    const marker = this.agentsById.get(agentId);
    if (!marker) return { status: 'offline', lastActiveAt: null, agentName: fallbackName };

    return this.toPresence(marker);
  }

  private toPresence(marker: AgentMarker): AgentPresence {
    const age = Date.now() - marker.lastActiveAt;
    const status: PresenceStatus =
      age < ONLINE_WINDOW_MS ? marker.availability : age < AWAY_WINDOW_MS ? 'away' : 'offline';

    return { status, lastActiveAt: new Date(marker.lastActiveAt).toISOString(), agentName: marker.agentName };
  }

  markTyping(sessionId: string, who: 'agent' | 'shopper', agentName?: string): void {
    this.typing.set(`${who}:${sessionId}`, Date.now() + TYPING_TTL_MS);
    if (who === 'agent' && agentName) this.typingAgentNames.set(sessionId, agentName);
  }

  isTyping(sessionId: string, who: 'agent' | 'shopper'): boolean {
    const expiresAt = this.typing.get(`${who}:${sessionId}`);
    if (expiresAt === undefined) return false;

    if (expiresAt <= Date.now()) {
      this.typing.delete(`${who}:${sessionId}`);
      return false;
    }

    return true;
  }

  typingAgentName(sessionId: string): string | undefined {
    return this.typingAgentNames.get(sessionId);
  }
}
